<template>
  <div class="gravitation-viz-3d">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F = G · (Mm/r²)$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 渲染区 -->
      <div ref="canvasContainer" class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
             <label>轨道距离 (r)</label>
             <span class="val">{{ radius.toFixed(1) }} <small>AU</small></span>
          </div>
          <div class="hud-item green">
             <label>引力大小 (F)</label>
             <span class="val">{{ gravitationForce.toFixed(2) }} <small>N</small></span>
          </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
           <div class="control-item">
              <div class="label-row">
                 <span class="label">轨道半径 (r)</span>
                 <span class="val">{{ radius }} AU</span>
              </div>
              <el-slider v-model="radius" :min="30" :max="120" size="small"></el-slider>
           </div>
           <div class="control-item">
              <div class="label-row">
                 <span class="label">中心天体质量 (M)</span>
                 <span class="val">{{ massM }} MT</span>
              </div>
              <el-slider v-model="massM" :min="10" :max="100" size="small"></el-slider>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>引力的大小与两物体质量乘积成正比，与距离的平方成反比。当距离增加一倍时，物体间的引力将衰减至原来的<strong> 1/4 </strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'GravitationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      radius: 60,
      massM: 40,
      running: true
    }
  },
  computed: {
    gravitationForce() {
      return (this.massM * 10) / (this.radius * this.radius) * 1000;
    }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
    this.scene = null;
    this.camera = null;
  },
  watch: {
    radius() { this.updateOrbit(); },
    massM() { this.updateSun(); }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x020617);
      this.camera = new THREE.PerspectiveCamera(50, container.clientWidth/container.clientHeight, 1, 1000);
      this.camera.position.set(80, 60, 100);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);

      const starsGeo = new THREE.BufferGeometry();
      const posArray = new Float32Array(2000 * 3);
      for(let i=0; i<6000; i++) posArray[i] = (Math.random() - 0.5) * 600;
      starsGeo.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
      this.scene.add(new THREE.Points(starsGeo, new THREE.PointsMaterial({ color: 0x475569, size: 0.5 })));

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      this.scene.add(new THREE.PointLight(0xf59e0b, 2.5, 400));

      this.sunGroup = new THREE.Group();
      this.scene.add(this.sunGroup);
      this.sun = new THREE.Mesh(new THREE.SphereGeometry(10, 32, 32), new THREE.MeshPhongMaterial({ color: 0xf97316, emissive: 0xea580c, emissiveIntensity: 1 }));
      this.sunGroup.add(this.sun);
      
      this.planetGroup = new THREE.Group();
      this.scene.add(this.planetGroup);
      this.planet = new THREE.Mesh(new THREE.SphereGeometry(4, 32, 32), new THREE.MeshPhongMaterial({ color: 0x3b82f6, specular: 0x60a5fa }));
      this.planetGroup.add(this.planet);

      this.forceArrow = new THREE.ArrowHelper(new THREE.Vector3(-1, 0, 0), new THREE.Vector3(0, 0, 0), 10, 0x10b981, 6, 3);
      this.planetGroup.add(this.forceArrow);

      this.updateOrbit();
      this.animate();
    },
    updateSun() { const s = 0.5 + (this.massM / 40) * 0.5; this.sun.scale.set(s, s, s); },
    updateOrbit() {
      if (this.orbit) this.scene.remove(this.orbit);
      const points = new THREE.EllipseCurve(0, 0, this.radius, this.radius).getPoints(128);
      this.orbit = new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), new THREE.LineBasicMaterial({ color: 0x3b82f6, transparent: true, opacity: 0.2 }));
      this.orbit.rotation.x = Math.PI / 2;
      this.scene.add(this.orbit);
    },
    animate() {
       if (this.frameId) cancelAnimationFrame(this.frameId);
       if (!this.running) return;
       this.frameId = requestAnimationFrame(this.animate);
       const time = Date.now() * 0.0005;
       const omega = 1 / Math.sqrt(Math.pow(this.radius, 1.5)) * 10;
       const angle = time * omega;
       if (this.planetGroup) {
         this.planetGroup.position.x = Math.cos(angle) * this.radius;
         this.planetGroup.position.z = Math.sin(angle) * this.radius;
         const dir = new THREE.Vector3().subVectors(new THREE.Vector3(0,0,0), this.planetGroup.position).normalize();
         this.forceArrow.setDirection(dir);
         const fLen = (this.gravitationForce / 12);
         this.forceArrow.setLength(Math.min(30, fLen), 5, 2.5);
       }
       if (this.sun) this.sun.rotation.y += 0.005;
       if (this.renderer && this.scene && this.camera) {
         this.renderer.render(this.scene, this.camera);
       }
    }
  }
}
</script>

<style scoped lang="scss">
.gravitation-viz-3d { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.8); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #64748b; font-weight: 700; } .val { font-size: 14px; color: #f1f5f9; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.green { border-left: 3px solid #10b981; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; .control-item { .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #2563eb; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
